import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Checkbox, Input, Label, Radio, Text } from '@pukka/ui-components';

import { AccountDeletionTnC } from './AccountDeletionTnC';

const REASON_OPTIONS = ['Business is permanently closed', 'Not interested in using MOKA', 'Privacy issues', 'Other reasons'];

interface AccountDeletionFormProps {
  readonly isSubmitting: boolean;
  readonly onSubmit: (reason: string, notes: string) => void;
}

function AccountDeletionForm({ isSubmitting, onSubmit }: AccountDeletionFormProps) {
  const [showTnC, setShowTnC] = useState(false);
  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      reason: '',
      notes: '',
      tncAccepted: false,
    },
  });

  const reasonValue = watch('reason');
  const tncAcceptedValue = watch('tncAccepted');
  const enableSubmit = !!reasonValue && tncAcceptedValue;

  const handleSubmitForm = handleSubmit((data) => {
    const { reason, notes } = data;

    onSubmit(reason, notes);
  });

  return (
    <>
      <Text>
        Please note that this action is <b>irreversible</b>. Once submitted, you will no longer have access to your account. If you have any
        remaining subscriptions, they will also be discontinued without a refund for the remaining subscription time.
      </Text>
      <form aria-label="account-deletion-form" className="flex flex-col gap-5 text-left" onSubmit={handleSubmitForm}>
        <div className="flex flex-col gap-2.5">
          <Label className="text-lg">Choose Deletion Reason</Label>
          <Radio.Group orientation="vertical">
            {REASON_OPTIONS.map((reason) => (
              <Radio key={reason} id={reason} value={reason} {...register('reason')}>
                {reason}
              </Radio>
            ))}
          </Radio.Group>
        </div>
        <div className="flex flex-col gap-2.5">
          <Label className="text-lg">Notes</Label>
          <Input placeholder="Give us any comment or feedback" {...register('notes')} />
        </div>
        <div className="rounded bg-neutral-100 p-4">
          <Checkbox id="tnc" data-testid="tnc-checkbox" {...register('tncAccepted')}>
            by clicking the button Submit, you agree to our{' '}
            <Text as="span" onClick={() => setShowTnC(true)} color="blue" className="cursor-pointer underline">
              terms and condition
            </Text>
          </Checkbox>
        </div>
        <Button variant="danger" type="submit" disabled={!enableSubmit} loading={isSubmitting}>
          Submit
        </Button>
      </form>
      <hr />
      <Text variant="caption-1">
        Changed your mind about deleting your account?{' '}
        <Text as="a" href="/" color="blue" className="hover:underline">
          Return to Backoffice
        </Text>
      </Text>

      <AccountDeletionTnC open={showTnC} onToggleModal={setShowTnC} />
    </>
  );
}

export { AccountDeletionForm };
